<template>
  <div>
    <div class="row">
      <div class="col-6">
        <PropertyValueTable propertyTitle="Order Estimates" helpTip="Estimated costs are based on stock value, production operating costs and despatch costs as the order enters production">
          <PropertyValueRow name="Estimated Stock Value">{{formatCurrency(order.calculatedEstimatedStockValue)}}</PropertyValueRow>
          <PropertyValueRow name="Estimated Production Cost">{{formatCurrency(order.calculatedEstimatedProductionValue)}}</PropertyValueRow>
          <PropertyValueRow name="Estimated Despatch Cost">{{formatCurrency(order.estimatedDespatchValue)}}</PropertyValueRow>
          <PropertyValueRow name="Total Estimated Cost" class="font-weight-bold">{{formatCurrency(totalEstimatedCost)}}</PropertyValueRow>
        </PropertyValueTable>
      </div>

      <div class="col-6">
        <PropertyValueTable propertyTitle="Pricing Details">
          <PropertyValueRow name="Order Subtotal">{{formatCurrency(orderSubTotal)}}</PropertyValueRow>
          <PropertyValueRow name="Order Tax">{{formatCurrency(order.totalTax)}}</PropertyValueRow>
          <PropertyValueRow name="Order Price">{{formatCurrency(order.totalPrice)}}</PropertyValueRow>
        </PropertyValueTable>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-6">
        <PropertyValueTable propertyTitle="Order Actual Costs" helpTip="Actual costs are based on stock value, production operating costs and despatch costs">
          <PropertyValueRow name="Actual Stock Value">{{formatCurrency(order.calculatedActualStockValue)}}</PropertyValueRow>
          <PropertyValueRow name="Actual Production Cost">{{formatCurrency(order.calculatedActualProductionValue)}}</PropertyValueRow>
          <PropertyValueRow name="Actual Despatch Cost">{{formatCurrency(order.calculatedActualDespatchValue)}}</PropertyValueRow>
          <PropertyValueRow name="NCR Cost" helpTip="Only calculated when linked NCRs are completed">{{formatCurrency(order.calculatedActualNCRCosts)}}</PropertyValueRow>
          <PropertyValueRow name="Total Actual Cost" class="font-weight-bold">{{formatCurrency(totalActualCost)}}</PropertyValueRow>
        </PropertyValueTable>
      </div>
      <div class="col-6">
        <PropertyValueTable propertyTitle="Margin Details">
          <PropertyValueRow name="Order Subtotal">{{formatCurrency(orderSubTotal)}}</PropertyValueRow>
          <PropertyValueRow name="NCR Adjustment" helpTip="Includes extra charges from NCRs minus any credits">{{formatCurrency(order.calculatedActualNCRSubTotal)}}</PropertyValueRow>
          <PropertyValueRow name="Adjusted Subtotal">{{formatCurrency(orderSubTotalAdjusted)}}</PropertyValueRow>

          <PropertyValueRow name="Estimate Margin">{{formatCurrency(orderSubTotalAdjusted - totalEstimatedCost)}}</PropertyValueRow>
          <PropertyValueRow name="Actual Margin">{{formatCurrency(orderSubTotalAdjusted - totalActualCost)}}</PropertyValueRow>
          <PropertyValueRow name="Estimate Gross Profit" v-if="orderSubTotalAdjusted > 0">{{formatPercent((orderSubTotalAdjusted - totalEstimatedCost) / orderSubTotalAdjusted * 100.0)}}</PropertyValueRow>
          <PropertyValueRow name="Actual Gross Profit" v-if="orderSubTotalAdjusted > 0">{{formatPercent((orderSubTotalAdjusted - totalActualCost) / orderSubTotalAdjusted * 100.0)}}</PropertyValueRow>
        </PropertyValueTable>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-6">
        <PropertyValueTable propertyTitle="Timings">
          <PropertyValueRow name="Order Entry Duration">{{formatDuration(order.orderEntryDuration * 60.0)}}</PropertyValueRow>
          <PropertyValueRow name="Created">{{formatDateTime(order.createdAt)}}</PropertyValueRow>
          <PropertyValueRow name="Estimated Due">{{formatDateTime(order.dateEstimated)}}</PropertyValueRow>
          <PropertyValueRow name="Customer Requested" v-if="order.dateRequested">{{formatDateTime(order.dateRequested)}}</PropertyValueRow>

          <PropertyValueRow name="Started Production">{{formatDateTime(order.dateProductionStarted)}}</PropertyValueRow>
          <PropertyValueRow name="Production Estimated Due">{{formatDateTime(order.dateEstimatedProductionCompleted)}}</PropertyValueRow>

          <PropertyValueRow name="Completed Production">{{formatDateTime(order.dateProductionCompleted)}}</PropertyValueRow>
          <PropertyValueRow name="Completed Despatch">{{formatDateTime(order.dateDespatchCompleted)}}</PropertyValueRow>
          <PropertyValueRow name="Closed">{{formatDateTime(order.dateClosed)}}</PropertyValueRow>
          <PropertyValueRow name="Time Spent in Production" v-if="order.statProductionDuration > 0">{{formatDuration(order.statProductionDuration * 60.0)}}</PropertyValueRow>
          <PropertyValueRow name="Time Spent in Despatch" v-if="order.statDespatchDuration > 0">{{formatDuration(order.statDespatchDuration * 60.0)}}</PropertyValueRow>
        </PropertyValueTable>
      </div>
      <div class="col-6">
        <PropertyValueTable propertyTitle="Reporting">
          <PropertyValueRow name="Reporting Code">{{formatLookupName(order.operationalReportingCode, ItemOperationalReportingCodeLookup)}}</PropertyValueRow>
          <PropertyValueRow name="Reporting Category">{{formatLookupName(reportingCategoryCode, ItemOperationalReportingCategoryTypeLookup)}}</PropertyValueRow>
          <PropertyValueRow name="Reporting Result">{{formatLookupName(order.operationalReportingResultType, OperationalReportingResultTypeLookup)}}</PropertyValueRow>
          <PropertyValueRow name="Reporting Note">{{formatHyphenEmptyValue(order.operationalReportingNote)}}</PropertyValueRow>
        </PropertyValueTable>
      </div>
    </div>
  </div>
</template>


<script>
import { ItemOperationalReportingCodeLookup, OperationalReportingResultTypeLookup, ItemOperationalReportingCategoryTypeLookup } from '@/store/types'

export default {
  name: 'OrderInsights',
  props: {
    order: Object
  },
  computed: {
    totalEstimatedCost() {
      return this.order.calculatedEstimatedStockValue + this.order.calculatedEstimatedProductionValue + this.order.estimatedDespatchValue;
    },
    totalActualCost() {
      return this.order.calculatedActualStockValue + this.order.calculatedActualProductionValue + this.order.calculatedActualDespatchValue + this.order.calculatedActualNCRCosts;
    },
    orderSubTotal() {
      return this.order.totalPrice - this.order.totalTax;
    },
    orderSubTotalAdjusted() {
      return this.order.totalPrice - this.order.totalTax + this.order.calculatedActualNCRSubTotal;
    },
    reportingCategoryCode() {
      if (this.order.operationalReportingCode) {
        const reportingCode = this.ItemOperationalReportingCodeLookup.find(code => code.value === this.order.operationalReportingCode);
        return reportingCode ? reportingCode.category : null;
      }
      return null;
    }
  },
  created() {
    this.ItemOperationalReportingCodeLookup = ItemOperationalReportingCodeLookup
    this.OperationalReportingResultTypeLookup = OperationalReportingResultTypeLookup
    this.ItemOperationalReportingCategoryTypeLookup = ItemOperationalReportingCategoryTypeLookup

  }
}
</script>